import React, { useEffect, useState } from 'react';
import { DatePicker, Form, Input, Modal, Select, message } from 'antd';
import dayjs from 'dayjs';
import api from '../../../lib/api';
import type { Project } from '../../../types/project';
import type { PlanTask } from '../../../types/plan';
import type { TimeEntry } from '../../../types/timeEntry';

type ManualEntryModalProps = {
  open: boolean;
  entry: TimeEntry | null;
  projects: Project[];
  tasks: PlanTask[];
  onCancel: () => void;
  onSaved: () => void;
};

type FormValues = {
  task_id: number | null;
  project_id: number | null;
  range: [dayjs.Dayjs, dayjs.Dayjs] | null;
  note: string | null;
};

const ManualEntryModal: React.FC<ManualEntryModalProps> = ({
  open,
  entry,
  projects,
  tasks,
  onCancel,
  onSaved,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (entry) {
      form.setFieldsValue({
        task_id: entry.task_id,
        project_id: entry.project_id,
        range: entry.ended_at ? [dayjs(entry.started_at), dayjs(entry.ended_at)] : null,
        note: entry.note,
      });
    } else {
      const now = dayjs();
      form.setFieldsValue({
        task_id: null,
        project_id: null,
        range: [now.subtract(1, 'hour'), now],
        note: '',
      });
    }
  }, [open, entry, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    if (!values.range) {
      message.error('请选起止时间');
      return;
    }

    const payload = {
      task_id: values.task_id ?? null,
      project_id: values.task_id ? null : values.project_id ?? null,
      started_at: values.range[0].toISOString(),
      ended_at: values.range[1].toISOString(),
      note: values.note ?? null,
    };

    setSaving(true);
    try {
      if (entry) {
        await api.timeEntries.update(entry.id, payload);
      } else {
        await api.timeEntries.create(payload);
      }
      onSaved();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={entry ? '编辑计时条目' : '手动补录'}
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="task_id" label="任务">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="不挂任务"
            options={tasks.map((task) => ({ value: task.id, label: task.title }))}
          />
        </Form.Item>

        <Form.Item name="project_id" label="项目（只在不挂任务时生效）">
          <Select
            allowClear
            placeholder="不挂项目"
            options={projects.map((project) => ({ value: project.id, label: project.name }))}
          />
        </Form.Item>

        <Form.Item name="range" label="起止时间" rules={[{ required: true, message: '请选起止时间' }]}>
          <DatePicker.RangePicker showTime className="w-full" />
        </Form.Item>

        <Form.Item name="note" label="备注">
          <Input.TextArea rows={2} placeholder="做了什么" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ManualEntryModal;
